import { User, UserManager } from 'oidc-client-ts';
import type { IdTokenClaims, UserManagerSettings } from 'oidc-client-ts';

/**
 * The slice of Cognito's user-pool API the sign-in page calls straight from
 * the browser: password and custom (Microsoft) sign-in, their challenges
 * (new password, MFA, MFA setup) and password reset. No AWS credentials are
 * involved; these are Cognito's public, client-id-authorised operations.
 */

export interface CognitoTokens {
  IdToken: string;
  AccessToken: string;
  RefreshToken: string;
  ExpiresIn: number;
  TokenType?: string;
}

export interface CognitoChallenge {
  name: string;
  session: string;
  /** The account's real username, which challenge answers must use. */
  username: string;
  parameters: Record<string, string>;
}

/** Where a sign-in stands: done, or waiting on a challenge. */
export type CognitoStep =
  | { tokens: CognitoTokens }
  | { challenge: CognitoChallenge };

/** A refusal from Cognito; `type` is its exception name. */
export class CognitoError extends Error {
  constructor(readonly type: string, message: string) {
    super(message);
  }
}

type Action =
  | 'InitiateAuth'
  | 'RespondToAuthChallenge'
  | 'AssociateSoftwareToken'
  | 'VerifySoftwareToken'
  | 'ForgotPassword'
  | 'ConfirmForgotPassword';

interface AuthResponse {
  AuthenticationResult?: CognitoTokens;
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

const call = async <T>(
  settings: UserManagerSettings,
  action: Action,
  body: object
): Promise<T> => {
  // The pool's issuer (the authority) lives on the same host as its API.
  const response = await fetch(`${new URL(settings.authority).origin}/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new CognitoError(
      String(data.__type ?? '')
        .split('#')
        .pop() ?? '',
      data.message ?? ''
    );
  }
  return data as T;
};

/** Cognito's SECRET_HASH, for app clients that have a secret. */
const secretHash = async (
  settings: UserManagerSettings,
  username: string
): Promise<string | undefined> => {
  if (!settings.client_secret) return undefined;
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(settings.client_secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const digest = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(username + settings.client_id)
  );
  return btoa(String.fromCharCode(...new Uint8Array(digest)));
};

const withSecretHash = async (
  settings: UserManagerSettings,
  params: Record<string, string> & { USERNAME: string }
) => {
  const hash = await secretHash(settings, params.USERNAME);
  return hash ? { ...params, SECRET_HASH: hash } : params;
};

const toStep = (response: AuthResponse, username: string): CognitoStep =>
  response.AuthenticationResult
    ? { tokens: response.AuthenticationResult }
    : {
        challenge: {
          name: response.ChallengeName ?? '',
          session: response.Session ?? '',
          username: response.ChallengeParameters?.USERNAME ?? username,
          parameters: response.ChallengeParameters ?? {},
        },
      };

export const initiateAuth = async (
  settings: UserManagerSettings,
  flow: 'USER_PASSWORD_AUTH' | 'CUSTOM_AUTH',
  username: string,
  parameters: Record<string, string> = {}
): Promise<CognitoStep> => {
  const response = await call<AuthResponse>(settings, 'InitiateAuth', {
    ClientId: settings.client_id,
    AuthFlow: flow,
    AuthParameters: await withSecretHash(settings, {
      USERNAME: username,
      ...parameters,
    }),
  });
  return toStep(response, username);
};

export const respondToChallenge = async (
  settings: UserManagerSettings,
  challenge: CognitoChallenge,
  answers: Record<string, string>,
  name = challenge.name
): Promise<CognitoStep> => {
  const response = await call<AuthResponse>(
    settings,
    'RespondToAuthChallenge',
    {
      ClientId: settings.client_id,
      ChallengeName: name,
      Session: challenge.session,
      ChallengeResponses: await withSecretHash(settings, {
        USERNAME: challenge.username,
        ...answers,
      }),
    }
  );
  return toStep(response, challenge.username);
};

/** Starts authenticator-app setup for an MFA_SETUP challenge; returns the secret to show. */
export const beginTotpSetup = async (
  settings: UserManagerSettings,
  challenge: CognitoChallenge
): Promise<{ secret: string; challenge: CognitoChallenge }> => {
  const response = await call<{ SecretCode: string; Session: string }>(
    settings,
    'AssociateSoftwareToken',
    { Session: challenge.session }
  );
  return {
    secret: response.SecretCode,
    challenge: { ...challenge, session: response.Session },
  };
};

/** Confirms the first authenticator code, then completes the MFA_SETUP challenge. */
export const completeTotpSetup = async (
  settings: UserManagerSettings,
  challenge: CognitoChallenge,
  code: string
): Promise<CognitoStep> => {
  const verified = await call<{ Status: string; Session: string }>(
    settings,
    'VerifySoftwareToken',
    {
      Session: challenge.session,
      UserCode: code,
      FriendlyDeviceName: 'Authenticator app',
    }
  );
  if (verified.Status !== 'SUCCESS') {
    throw new CognitoError('EnableSoftwareTokenMFAException', 'Code rejected');
  }
  return respondToChallenge(
    settings,
    { ...challenge, session: verified.Session },
    {},
    'MFA_SETUP'
  );
};

/** Sends a reset code; resolves to where it went (e.g. "j***@e***"), if Cognito says. */
export const requestPasswordReset = async (
  settings: UserManagerSettings,
  email: string
): Promise<string | undefined> => {
  const response = await call<{
    CodeDeliveryDetails?: { Destination?: string };
  }>(settings, 'ForgotPassword', {
    ClientId: settings.client_id,
    Username: email,
    SecretHash: await secretHash(settings, email),
  });
  return response.CodeDeliveryDetails?.Destination;
};

export const confirmPasswordReset = async (
  settings: UserManagerSettings,
  email: string,
  code: string,
  password: string
): Promise<void> => {
  await call(settings, 'ConfirmForgotPassword', {
    ClientId: settings.client_id,
    Username: email,
    ConfirmationCode: code,
    Password: password,
    SecretHash: await secretHash(settings, email),
  });
};

/** A password nobody knows, for invited users who sign in without one. */
export const unusablePassword = () =>
  btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(24)))) +
  'aA1!';

/** JWT payload as claims; decodes UTF-8 so non-ASCII names survive. */
const decodeClaims = (jwt: string): IdTokenClaims => {
  const base64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

/**
 * Stores Cognito's tokens exactly where react-oidc-context keeps a hosted-UI
 * session, through a UserManager built from the app's own settings, so the
 * storage key is whatever oidc-client-ts itself uses. Silent renew is off on
 * this throwaway instance; the app's own one takes over after the reload.
 */
export const storeSession = (
  settings: UserManagerSettings,
  tokens: CognitoTokens
) =>
  new UserManager({ ...settings, automaticSilentRenew: false }).storeUser(
    new User({
      id_token: tokens.IdToken,
      access_token: tokens.AccessToken,
      refresh_token: tokens.RefreshToken,
      token_type: tokens.TokenType ?? 'Bearer',
      profile: decodeClaims(tokens.IdToken),
      expires_at: Math.floor(Date.now() / 1000) + tokens.ExpiresIn,
      // No `scope`: API-issued tokens carry none, and refreshing without one
      // keeps the refresh request valid.
    })
  );
